import type { ReactElement } from 'react'
import { Route } from 'react-router-dom'
import { AreaGate } from '../../core/areas/AreaGate'
import { flattenNav } from '../../core/router/navConfig'
import { PermissionGate } from '../../core/router/guards'
import { PlaceholderPage } from '../../shared/components/PlaceholderPage'
import {
  AuditoriaPage, FarmAgendaPage, FarmConfinamentoPage, FarmContatosPage, FarmDocumentosPage, FarmEquipePage,
  FarmFinanceiroPage, FarmFlorestaPage, FarmFundoPage, FarmImoveisPage, FarmImovelDetalhePage, FarmInicioPage,
  FarmLavouraPage, FarmMapaPage, FarmParceirosPage, FarmSafrasPage, ResourceFormPage, ResourceListPage,
  UsuarioFormPage, comPermissao,
} from '../../core/router/telas'
import { FARM_CARGOS_PATH, FARM_USUARIOS_PATH, farmCargosConfig, farmUsuariosConfig } from './administracao'
import { farmNav } from './nav'

const RAIZ = '/farm'

/** Registro path → componente dos itens do menu da Farm. Item sem entrada aqui cai
 *  no PlaceholderPage automaticamente. A Administração tem rotas próprias, abaixo,
 *  porque lista, criação e edição são três endereços. */
const PAGES: Record<string, ReactElement> = {
  '/farm': <FarmInicioPage />,
  '/farm/mapa': <FarmMapaPage />,
  '/farm/agenda': <FarmAgendaPage />,
  '/farm/imoveis': <FarmImoveisPage />,
  '/farm/documentos': <FarmDocumentosPage />,
  '/farm/fundo': <FarmFundoPage />,
  '/farm/lavoura': <FarmLavouraPage />,
  '/farm/confinamento': <FarmConfinamentoPage />,
  '/farm/floresta': <FarmFlorestaPage />,
  '/farm/equipe': <FarmEquipePage />,
  '/farm/financeiro': <FarmFinanceiroPage />,
  '/farm/cadastros/parceiros': <FarmParceirosPage />,
  '/farm/cadastros/safras': <FarmSafrasPage />,
  '/farm/cadastros/contatos': <FarmContatosPage />,
}

const DA_ADMINISTRACAO = new Set([FARM_USUARIOS_PATH, FARM_CARGOS_PATH, '/farm/auditoria'])

/** As rotas do HubVerticalFarm, cada tela com o portão da key que o item do menu
 *  declara, pelo roteiro de docs/areas/05-como-adicionar.md. */
export function rotasDaFarm(): ReactElement {
  return (
    <Route path={RAIZ} element={<AreaGate area="farm" />}>
      {flattenNav(farmNav)
        .filter((item) => !DA_ADMINISTRACAO.has(item.path))
        .map((item) => {
          const tela = comPermissao(item, PAGES[item.path] ?? <PlaceholderPage title={item.label} />)
          // O Início é a raiz da área: entra como índice, os módulos com o próprio endereço.
          return item.path === RAIZ
            ? <Route key={item.path} index element={tela} />
            : <Route key={item.path} path={item.path} element={tela} />
        })}

      {/* Os detalhes, fora do menu: o portão vai à mão, com a key do módulo. */}
      <Route path="/farm/imoveis/:imovelId" element={<PermissionGate permission="farm_imoveis.view"><FarmImovelDetalhePage /></PermissionGate>} />
      <Route path="/farm/mapa/:imovelId" element={<PermissionGate permission="farm_mapa.view"><FarmMapaPage /></PermissionGate>} />

      {/* Administração: as telas do Administrativo, as mesmas keys. */}
      <Route path={FARM_USUARIOS_PATH} element={<PermissionGate permission="users.view"><ResourceListPage config={farmUsuariosConfig} /></PermissionGate>} />
      <Route path={`${FARM_USUARIOS_PATH}/new`} element={<PermissionGate permission="users.view"><UsuarioFormPage variante="backoffice" base={FARM_USUARIOS_PATH} /></PermissionGate>} />
      <Route path={`${FARM_USUARIOS_PATH}/:uuid`} element={<PermissionGate permission="users.view"><UsuarioFormPage variante="backoffice" base={FARM_USUARIOS_PATH} /></PermissionGate>} />
      <Route path={FARM_CARGOS_PATH} element={<PermissionGate permission="users.view"><ResourceListPage config={farmCargosConfig} /></PermissionGate>} />
      <Route path={`${FARM_CARGOS_PATH}/new`} element={<PermissionGate permission="users.view"><ResourceFormPage config={farmCargosConfig} /></PermissionGate>} />
      <Route path={`${FARM_CARGOS_PATH}/:uuid`} element={<PermissionGate permission="users.view"><ResourceFormPage config={farmCargosConfig} /></PermissionGate>} />
      <Route path="/farm/auditoria" element={<PermissionGate permission="audit.view"><AuditoriaPage /></PermissionGate>} />
    </Route>
  )
}
